/** @prettier */

/**
 * Hyozanグループユニットクーラ編集ダイアログ
 */

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';

//MTH component import
import TransferList from '../../component/TransferList';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  getCompositionsGroupsByGroupID,
  patchCompositionsGroups,
  getCompositionsGroups,
} from '../../API/API_Collection';
//TypeDefine import
import {regunitType} from '../../Type/TypeDef';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {iconWhite, limit} from '../../define';

import {useTranslation} from 'react-i18next';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

const GroupUnitEditDialog = (props: {
  unitSystem: 'RT' | 'Modbus';
  displayGroupInfo: apiIF.res.getCompositionsGroupsByGroupID;
  setDisplayGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGroupsByGroupID | null>
  >;
}) => {
  //パスパラメータ取得
  const {siteID, groupID} = useParams();

  //prettier-ignore
  const [unregisteredIUInit, setUnregisteredIUInit] = useState<regunitType[]>([]); //未登録室内機（初期値）
  const [unregisteredIU, setUnregisteredIU] = useState<regunitType[]>([]); //未登録室内機
  const [registeredIUInit, setRegisteredIUInit] = useState<regunitType[]>([]); //登録室内機（初期値）
  const [registeredIU, setRegisteredIU] = useState<regunitType[]>([]); //登録室内機
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //SAVEボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    //パスパラメータを取得できない場合
    //グループの情報を取得できない場合
    if (
      siteID === undefined ||
      groupID === undefined ||
      props.displayGroupInfo === null
    ) {
      return;
    }

    setRegisteredIUInit(props.displayGroupInfo['unit-informations']);
    setRegisteredIU(props.displayGroupInfo['unit-informations']);

    //サイトに登録されているすべてのグループの情報を取得
    getCompositionsGroups(siteID, {'max-result': 400}).then(groupInfo => {
      //未登録室内機の抽出
      //x、y座標情報を付加する
      let temp: regunitType[] = [];
      if (props.unitSystem === 'RT') {
        temp = groupInfo['unregistered-rt-unit'].map(unitInfo => ({
          'unit-id': unitInfo['unit-id'],
          'unit-name': unitInfo['indoor-unit-name'],
          'x-coordinate': '-1',
          'y-coordinate': '-1',
        }));
      } else if (props.unitSystem === 'Modbus') {
        temp = groupInfo['unregistered-modbus-unit'].map(unitInfo => ({
          'unit-id': unitInfo['unit-id'],
          'unit-name': unitInfo['indoor-unit-name'],
          'x-coordinate': '-1',
          'y-coordinate': '-1',
        }));
      }

      setUnregisteredIUInit(temp);
      setUnregisteredIU(temp);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID, groupID, props.unitSystem, props.displayGroupInfo]);

  /**
   * Unit Editボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenEditDialog(true);
  };

  /**
   * SAVEボタン押下時処理
   */
  const editUnit = () => {
    //パスパラメータを取得できない場合
    //グループの情報を取得できない場合
    if (siteID === undefined || groupID === undefined) {
      return;
    }

    setSaveBtnDisabled(true);
    setAPIWait(true);

    //編集情報の作成
    const editData: apiIF.reqParameters.patchCompositionsGroups = {
      //リストの右側に選択したユニットを登録室内機とする
      'unit-informations': registeredIU.map(info => ({
        'unit-id': info['unit-id'],
        'x-coordinate': info['x-coordinate'],
        'y-coordinate': info['y-coordinate'],
      })),
    };
    patchCompositionsGroups(siteID, groupID, editData)
      .then(() => {
        setUnregisteredIUInit(unregisteredIU);
        setRegisteredIUInit(registeredIU);
        //グループの情報取得
        return getCompositionsGroupsByGroupID(siteID, groupID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));
        props.setDisplayGroupInfo(response);
        setRegisteredIU(response['unit-informations']);
        setSaveBtnDisabled(false);
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setSaveBtnDisabled(false);
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setUnregisteredIU(unregisteredIUInit);
    setRegisteredIU(registeredIUInit);
    setIsOpenEditDialog(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleClickOpen}
        id="btnUnitEdit"
        startIcon={
          <img
            className="groupSetting__contents__button__icon"
            src={iconWhite.EDIT.src}
            alt={`${trans(
              `GroupUnitEditDialog.image.alternativeText.${iconWhite.EDIT.alt}`
            )}`}
          />
        }>
        {trans('GroupUnitEditDialog.buttonTxt.unitEdit')}
      </Button>
      <Dialog
        open={isOpenEditDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        id="mv-groupIuRegistDialog"
        fullWidth={true}
        maxWidth="lg">
        <DialogTitle id="form-dialog-title">
          {trans('GroupUnitEditDialog.dialogBox.title')}
        </DialogTitle>
        {/* リスト表示（左側に未登録室内機、右側に登録室内機の情報を付与) */}
        <DialogContent>
          <DialogContentText>
            {trans('GroupUnitEditDialog.dialogBox.msg')}
            {registeredIU.length > limit.REGISTRATION_NUM_GROUP_IU_MAX ? (
              <Box className="groupSetting__contents__message--iuOver">
                {trans('GroupUnitEditDialog.dialogBox.errorMsg.registerLimit')}
              </Box>
            ) : (
              <></>
            )}
          </DialogContentText>
          <TransferList
            left={unregisteredIU}
            setLeft={setUnregisteredIU}
            right={registeredIU}
            setRight={setRegisteredIU}
          />
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('GroupUnitEditDialog.dialogBox.buttonTxt.cancel')}
            id={'mv-groupUnitEditCancelBtn'}
          />
          <PrimaryButton
            onClick={editUnit}
            buttonText={trans('GroupUnitEditDialog.dialogBox.buttonTxt.save')}
            id="mv-groupUnitEditSaveBtn"
            disabled={
              saveBtnDisabled ||
              registeredIU.length > limit.REGISTRATION_NUM_GROUP_IU_MAX
            }
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-groupUnitEditErrDlgBtn'}
      />
    </>
  );
};
export default GroupUnitEditDialog;
